import React, { useEffect, useState } from "react";
import { setGlobalLoading } from "../context/LoaderController";
import { fetchMemberCount, fetchDistrictAssemblyCount } from "../services/api";
import {
  LOCALSTORAGE_SELECTED_STATE_CODE,
  LOCALSTORAGE_SELECTED_STATE_NAME,
  PAGE_TITLE_MEMBERSHIP_COUNT,
  NO_STATE_SELECTED_MESSAGE,
  LOADING_MEMBERSHIP_COUNT,
  ERROR_FETCH_MEMBER_COUNT,
  STATE_LABEL,
  MEMBER_COUNT_TOTAL_LABEL,
  MEMBER_COUNT_PAID_LABEL,
  MEMBER_COUNT_UNPAID_LABEL,
} from "../components/constants";
import "../App.css";

export default function MembershipCount() {
  const storedStateId = localStorage.getItem(LOCALSTORAGE_SELECTED_STATE_CODE);
  const storedStateName =
    localStorage.getItem(LOCALSTORAGE_SELECTED_STATE_NAME) || "";

  const [stateId] = useState(storedStateId ?? "");

  const [memberCountTotal, setMemberCountTotal] = useState(0);
  const [memberCountPaid, setMemberCountPaid] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [districtAssemblies, setDistrictAssemblies] = useState([]);
  const [showDA, setShowDA] = useState(false);
  const [daLoading, setDaLoading] = useState(false);

  const memberCountUnpaid = Math.max(memberCountTotal - memberCountPaid, 0);

  /* ================= LOAD MEMBER COUNTS FIRST ================= */
  useEffect(() => {
    if (!stateId) return;

    const loadCounts = async () => {
      setGlobalLoading(true);
      setError("");

      try {
        const data = await fetchMemberCount(stateId);
        setMemberCountTotal(Number(data.memberCountTotal || 0));
        setMemberCountPaid(Number(data.memberCount || 0));
      } catch (err) {
        console.error(err);
        setError(ERROR_FETCH_MEMBER_COUNT);
      } finally {
        setGlobalLoading(false);
      }
    };

    loadCounts();
  }, [stateId]);

  /* ================= TOGGLE DISTRICT ASSEMBLY ================= */
  const handleToggleDistrictAssemblies = async () => {
    if (!stateId) return;

    // Hide if already visible
    if (showDA) {
      setShowDA(false);
      return;
    }

    // Show instantly if already fetched
    if (districtAssemblies.length > 0) {
      setShowDA(true);
      return;
    }

    // First-time fetch
    setDaLoading(true);
    setShowDA(true);

    try {
      const da = await fetchDistrictAssemblyCount(stateId);
      setDistrictAssemblies(Array.isArray(da) ? da : []);
    } catch (err) {
      console.error(err);
      setDistrictAssemblies([]);
    } finally {
      setDaLoading(false);
    }
  };

  /* ================= NO STATE SELECTED ================= */
  if (!stateId) {
    return (
      <div className="main-container">
        <h2 className="section-title">{PAGE_TITLE_MEMBERSHIP_COUNT}</h2>
        <p>
          {NO_STATE_SELECTED_MESSAGE.split("Home")[0]}
          <a href="/" className="link">Home</a> page first.
        </p>
      </div>
    );
  }

  return (
    <div className="main-container">
      <h2 className="section-title">{PAGE_TITLE_MEMBERSHIP_COUNT}</h2>

      <span className="iyc-badge">
        {STATE_LABEL} <strong>{storedStateName || stateId}</strong>
      </span>

      {loading && <p>{LOADING_MEMBERSHIP_COUNT}</p>}
      {error && <p style={{ color: "red" }}>❌ {error}</p>}

      {/* ================= MEMBER COUNT SUMMARY ================= */}
      {!loading && !error && (
        <div className="votes-summary-container">
          <div className="votes-summary-box did-not-vote">
            <div className="votes-summary-label">
              {MEMBER_COUNT_TOTAL_LABEL}
            </div>
            <div className="votes-summary-value">{memberCountTotal}</div>
          </div>

          <div className="votes-summary-box">
            <div className="votes-summary-label">
              {MEMBER_COUNT_PAID_LABEL}
            </div>
            <div className="votes-summary-value">{memberCountPaid}</div>
          </div>

          <div className="votes-summary-box did-not-vote">
            <div className="votes-summary-label">
              {MEMBER_COUNT_UNPAID_LABEL}
            </div>
            <div className="votes-summary-value">{memberCountUnpaid}</div>
          </div>
        </div>
      )}

      {/* ================= TOGGLE BUTTON ================= */}
      <div style={{ marginTop: "24px" }}>
        <button
          className="iyc-primary-btn"
          onClick={handleToggleDistrictAssemblies}
          disabled={daLoading}
        >
          {daLoading
            ? "Loading…"
            : showDA
            ? "Hide District Assembly Count"
            : "Show District Assembly Member Count"}
        </button>
      </div>

      {/* ================= DISTRICT / ASSEMBLY MEMBER COUNT ================= */}
      {showDA && (
        <div className="table-container" style={{ marginTop: "32px" }}>
          <h3 className="section-subtitle">District Assembly Member Count</h3>

          {daLoading && <p>Loading district assembly counts…</p>}

          {!daLoading && districtAssemblies.length === 0 && (
            <p>No district assembly member count available for this state.</p>
          )}

          {!daLoading && districtAssemblies.length > 0 && (
            <table className="common-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>District</th>
                  <th>Assembly</th>
                  <th>Members</th>
                </tr>
              </thead>
              <tbody>
                {districtAssemblies.map((row, i) => (
                  <tr key={`${row.assemblyCode}-${i}`}>
                    <td>{i + 1}</td>
                    <td>{row.districtName}</td>
                    <td>{row.assemblyName}</td>
                    <td>{row.memberCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
